import React from 'react'
import { Link } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Switch from '@mui/material/Switch'
import { VyneSelect } from '@vynedental/design-system'

import VyneTooltip from '../../../components/VyneTooltip/VyneTooltip'
import { PracticeLocationSchedulingConfiguration } from '../../../models/v2/Practice'

import { AVAILABILITY_MONTHS_OPTIONS } from './constants'

const moduleName = 'appointment-settings-locations-list'

type Props = {
    configuration: PracticeLocationSchedulingConfiguration
    onUpdate: (configuration: PracticeLocationSchedulingConfiguration) => void
    isOnlineSchedulingWritebackEnabled: boolean
    practiceId: string
    showSidebookingPlaceholder: boolean
    earlierAvailabilityToggleEnabled: boolean
    isDentrixAgent: boolean
    isDentrixEnterprise: boolean
    isLegacyAgent: boolean
}

const AppointmentsFields = ({
    configuration,
    onUpdate,
    isOnlineSchedulingWritebackEnabled,
    practiceId,
    showSidebookingPlaceholder,
    earlierAvailabilityToggleEnabled,
    isDentrixAgent,
    isDentrixEnterprise,
    isLegacyAgent,
}: Props) => {
    const showWritebackTooltip = !isOnlineSchedulingWritebackEnabled
    const scheduleAppointmentOptions = [
        { label: 'Allow Booking', value: 'allow', disabled: !isOnlineSchedulingWritebackEnabled },
        { label: 'Request Booking', value: 'request' },
    ]

    const askEarlierAvailabilityEnabled = configuration.ask_about_earlier_availability !== false
    const earlierAvailabilityDescription = isDentrixAgent
        ? "When enabled, patients are asked at the end of scheduling if they'd like to be seen sooner. If they opt in, they're added to the ASAP list in the location's practice management system."
        : "When enabled, patients are asked at the end of scheduling if they'd like to be seen sooner. If they opt in, their appointment is marked as 'sooner if possible'."
    const showEarlierAvailabilitySection = earlierAvailabilityToggleEnabled && !isDentrixEnterprise && !isLegacyAgent

    return (
        <>
            <Grid
                item={true}
                xs={12}
                sm={6}
                md={3}
                lg={2.3}
                xl={2.3}
                className={`${moduleName}__table-item details`}
                style={{ borderBottom: 'none', paddingTop: 16 }}
            >
                <label className={`${moduleName}__input-label`}>
                    Appointment Scheduling
                    {showWritebackTooltip && (
                        <span style={{ marginLeft: 4, display: 'inline-flex' }}>
                            <VyneTooltip>
                                <span>
                                    This location cannot accept Direct Booking because PMS Writeback is not enabled. To
                                    enable Writeback, please visit the{' '}
                                    <Link
                                        style={{ color: '#fff', textDecoration: 'underline' }}
                                        to={`/writeback-settings/${practiceId}`}
                                    >
                                        PMS Writeback Settings
                                    </Link>{' '}
                                    page.
                                </span>
                            </VyneTooltip>
                        </span>
                    )}
                </label>
                <VyneSelect
                    dataTestId="schedule-appointment-select"
                    value={configuration.schedule_appointment}
                    onChange={(value: 'allow' | 'request') =>
                        onUpdate({
                            ...configuration,
                            schedule_appointment: value,
                        })
                    }
                    style={{ width: '100%' }}
                    options={scheduleAppointmentOptions}
                />
            </Grid>
            <Grid
                item={true}
                xs={12}
                sm={6}
                md={3}
                lg={2.3}
                xl={2.3}
                className={`${moduleName}__table-item details`}
                style={{ borderBottom: 'none', paddingTop: 16 }}
            >
                <label className={`${moduleName}__input-label`}>
                    Allow new patient bookings up to
                    <VyneTooltip
                        iconMarginLeft={4}
                        iconFontSize={14}
                        text="Controls how many months ahead patients can see and book appointments."
                    />
                </label>
                <VyneSelect
                    dataTestId="new-patient-availability-months-select"
                    value={configuration.new_patient_availability_months?.toString() ?? ''}
                    onChange={(value: string) =>
                        onUpdate({
                            ...configuration,
                            new_patient_availability_months: value ? Number(value) : null,
                        })
                    }
                    style={{ width: '100%' }}
                    options={AVAILABILITY_MONTHS_OPTIONS}
                />
            </Grid>
            <Grid
                item={true}
                className={`${moduleName}__table-item details`}
                xs={6}
                sm={3}
                md={3}
                lg={2.3}
                xl={2.3}
                style={{ borderBottom: 'none', paddingTop: 16 }}
            >
                <label className={`${moduleName}__input-label`}>
                    Allow existing patient bookings up to
                    <VyneTooltip
                        iconMarginLeft={4}
                        iconFontSize={14}
                        text="Controls how many months ahead patients can see and book appointments."
                    />
                </label>
                <VyneSelect
                    dataTestId="existing-patient-availability-months-select"
                    value={configuration.existing_patient_availability_months?.toString() ?? ''}
                    onChange={(value: string) =>
                        onUpdate({
                            ...configuration,
                            existing_patient_availability_months: value ? Number(value) : null,
                        })
                    }
                    style={{ width: '100%' }}
                    options={AVAILABILITY_MONTHS_OPTIONS}
                />
            </Grid>
            {showSidebookingPlaceholder ? (
                <Grid
                    item={true}
                    xs={4}
                    sm={3}
                    md={2}
                    lg={1}
                    className={`${moduleName}__table-item details ${moduleName}__checkbox`}
                    style={{ borderBottom: 'none' }}
                />
            ) : (
                <Grid
                    item={true}
                    className={`${moduleName}__table-item`}
                    sm={3}
                    md={3}
                    lg={1}
                    style={{ borderBottom: 'none' }}
                />
            )}
            {showEarlierAvailabilitySection && (
                <Grid
                    item={true}
                    xs={12}
                    className={`${moduleName}__table-item details ${moduleName}__earlier-availability`}
                    style={{ borderBottom: 'none' }}
                >
                    <div className={`${moduleName}__earlier-availability-row`}>
                        <Switch
                            data-testid="ask-about-earlier-availability-switch"
                            checked={askEarlierAvailabilityEnabled}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                onUpdate({
                                    ...configuration,
                                    ask_about_earlier_availability: event.target.checked,
                                })
                            }
                            size="small"
                        />
                        <div className={`${moduleName}__earlier-availability-text`}>
                            <div className={`${moduleName}__earlier-availability-title`}>
                                Ask patients about earlier availability
                            </div>
                            <div className={`${moduleName}__earlier-availability-description`}>
                                {earlierAvailabilityDescription}
                            </div>
                        </div>
                    </div>
                </Grid>
            )}
        </>
    )
}

export default AppointmentsFields
